import type { UserRole } from '@/entities/session/types'
import { getPhoneDigits } from '@/shared/lib/phoneMask'

/** Labels dos perfis para exibição. */
export const PROFILE_ROLE_LABEL: Record<UserRole, string> = {
  doctor: 'Médico',
  patient: 'Paciente',
  admin: 'Administrador',
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Parseia o texto de especialidades (separadas por vírgula, ponto-e-vírgula ou quebra de linha).
 */
export function parseSpecialtiesText(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

/**
 * Valida os dados do formulário de perfil antes de salvar.
 * Retorna mensagem de erro ou null se válido.
 */
export function validateProfileSave(params: {
  displayName: string
  contactEmail: string
}): string | null {
  const normalizedDisplayName = params.displayName.trim()
  if (!normalizedDisplayName) {
    return 'Informe um nome de exibição.'
  }

  const normalizedContactEmail = params.contactEmail.trim()
  if (normalizedContactEmail && !EMAIL_REGEX.test(normalizedContactEmail)) {
    return 'Informe um email de contato válido.'
  }

  return null
}

export type UsageBarColor = 'red' | 'amber' | 'mint'

/**
 * Retorna a cor da barra de uso (requisições ou tokens) conforme a proporção.
 */
export function getUsageBarColor(used: number, limit: number): UsageBarColor {
  if (limit <= 0) return 'mint'
  const ratio = used / limit
  if (ratio >= 1) return 'red'
  if (ratio >= 0.8) return 'amber'
  return 'mint'
}

const USAGE_BAR_CLASS: Record<UsageBarColor, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  mint: 'bg-[var(--color-mint-strong)]',
}

/**
 * Retorna a classe Tailwind para a barra de uso conforme a proporção.
 */
export function getUsageBarColorClass(used: number, limit: number): string {
  return USAGE_BAR_CLASS[getUsageBarColor(used, limit)]
}

/**
 * Indica se o formulário de perfil está com alterações não salvas.
 */
export function isProfileFormDirty(params: {
  displayName: string
  avatarPreview: string
  printLogoPreview: string
  crm: string
  phone1: string
  phone2: string
  printName: string
  contactEmail: string
  personType: string
  cpfCnpj: string
  pis: string
  tradeName: string
  currentSpecialties: string[]
  user: {
    name?: string
    avatarUrl?: string
    printLogoUrl?: string
    crm?: string
    phone1?: string
    phone2?: string
    telefone1?: string
    telefone2?: string
    printName?: string
    contactEmail?: string
    personType?: string
    cpfCnpj?: string
    pis?: string
    tradeName?: string
    specialties?: string[]
    especialidades?: string[]
  } | null
}): boolean {
  const { user } = params
  if (!user) return false

  const userSpecialties = user.specialties ?? user.especialidades ?? []
  return (
    params.displayName.trim() !== (user.name ?? '').trim() ||
    (params.avatarPreview || '') !== (user.avatarUrl ?? '') ||
    (params.printLogoPreview || '') !== (user.printLogoUrl ?? '') ||
    params.crm.trim() !== (user.crm ?? '').trim() ||
    getPhoneDigits(params.phone1) !== getPhoneDigits(user.phone1 ?? user.telefone1 ?? '') ||
    getPhoneDigits(params.phone2) !== getPhoneDigits(user.phone2 ?? user.telefone2 ?? '') ||
    params.printName.trim() !== (user.printName ?? '').trim() ||
    params.contactEmail.trim().toLowerCase() !== (user.contactEmail ?? '').trim().toLowerCase() ||
    params.personType !== (user.personType ?? '') ||
    params.cpfCnpj.trim() !== (user.cpfCnpj ?? '').trim() ||
    params.pis.trim() !== (user.pis ?? '').trim() ||
    params.tradeName.trim() !== (user.tradeName ?? '').trim() ||
    params.currentSpecialties.join('|') !== userSpecialties.join('|')
  )
}
